import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfigData, CuttingService, IBaskets, IConfig, IMachineDetails } from '@shared/services/cutting.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-machine-dialog',
	templateUrl: './create-update-machine.html',
	styleUrls: ['./create-update-machine.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateMachineDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	machine: IMachineDetails;
	httpError = '';
	loading = false;
	availableBaskets: IBaskets;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ConfigData,
		public dialogRef: MatDialogRef<CreateUpdateMachineDialogComponent>,
		private cuttingService: CuttingService,
		private fb: FormBuilder,
	) {
		this.machine = {
			self: null,
			name: null,
			divisionType: null,
			divisionTypeTranslation: null,
		};

		if (data.editMachine) {
			this.machine = { ...data.editMachine };
		}
	}

	getMachine(): IMachineDetails {
		return this.data.editMachine;
	}

	getOtherMachines(): IMachineDetails[] {
		return this.data.otherMachines;
	}

	getConfig(): string {
		return this.data.self;
	}

	ngOnInit(): void {
		this.cuttingService.getBaskets().subscribe((baskets) => {
			this.availableBaskets = baskets;
		});

		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.machine.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			divisionType: [this.machine.divisionType],
		});

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.machine) {
					this.machine[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave() {
		this.httpError = '';
		if (this.form.invalid || this.loading) return;
		this.loading = true;

		const createMachineConfig = (machine) => ({
			name: machine.name,
			self: machine.self,
			divisionType: machine.divisionType,
		});

		const machines = [createMachineConfig(this.machine), ...this.getOtherMachines().map(createMachineConfig)];

		const config: IConfig = {
			self: this.getConfig(),
			prohibitedColorPairing: this.data.configData.prohibitedColorPairing,
			allowFrameAndSashInSameBasket: this.data.configData.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: this.data.configData.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: this.data.configData.generateRodsForWaste,
			extraRodsPercentage: this.data.configData.extraRodsPercentage,
			machines,
		};

		const saveObservable = this.machine.self ? this.cuttingService.updateConfig(config) : this.cuttingService.createConfig(config);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}
}
